import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Table, Modal, Spin, Card, message, Switch, Space, Popconfirm, Alert } from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

export default function GameStepsManager() {
  const [form] = Form.useForm();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadSteps();
  }, []);

  const loadSteps = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getGameSteps();
      if (response.data.success) {
        setSteps(response.data.steps);
      }
    } catch (error) {
      message.error('خطا در بارگذاری مراحل بازی');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      let response;
      if (editingId) {
        response = await adminAPI.updateGameStep(editingId, values);
      } else {
        response = await adminAPI.createGameStep(values);
      }
      if (response.data.success) {
        message.success('مرحله با موفقیت ذخیره شد!');
        form.resetFields();
        setEditingId(null);
        setIsModalVisible(false);
        loadSteps();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'خطا در ذخیره مرحله');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    form.setFieldsValue(record);
    setEditingId(record._id);
    setIsModalVisible(true);
  };

  const handleToggle = async (record) => {
    try {
      const response = await adminAPI.toggleGameStep(record._id);
      if (response.data.success) {
        setSteps((prev) => prev.map((s) => (s._id === record._id ? response.data.step : s)));
      }
    } catch (error) {
      message.error('خطا در تغییر وضعیت مرحله');
    }
  };

  const handleDelete = async (record) => {
    try {
      const response = await adminAPI.deleteGameStep(record._id);
      if (response.data.success) {
        message.success('مرحله حذف شد');
        loadSteps();
      }
    } catch (error) {
      message.error('خطا در حذف مرحله');
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= steps.length) return;
    const reordered = [...steps];
    const [item] = reordered.splice(index, 1);
    reordered.splice(target, 0, item);
    setSteps(reordered);
    try {
      await adminAPI.reorderGameSteps(reordered.map((s) => s._id));
    } catch (error) {
      message.error('خطا در بازچینش مراحل');
      loadSteps();
    }
  };

  const columns = [
    {
      title: 'ترتیب',
      width: 90,
      render: (_, record, index) => (
        <Space size={2}>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => move(index, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === steps.length - 1}
            onClick={() => move(index, 1)}
          />
        </Space>
      )
    },
    {
      title: 'آیکون',
      dataIndex: 'icon',
      width: 60,
      render: (icon) => <span style={{ fontSize: 20 }}>{icon}</span>
    },
    {
      title: 'عنوان',
      dataIndex: 'title'
    },
    {
      title: 'کلید',
      dataIndex: 'key',
      render: (text) => <code>{text}</code>
    },
    {
      title: 'توضیح',
      dataIndex: 'description',
      ellipsis: true
    },
    {
      title: 'فعال',
      width: 90,
      render: (_, record) => (
        <Switch checked={record.enabled} onChange={() => handleToggle(record)} />
      )
    },
    {
      title: 'عمل',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            ویرایش
          </Button>
          <Popconfirm title="این مرحله حذف شود؟" okText="بله" cancelText="خیر" onConfirm={() => handleDelete(record)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card title="مدیریت مراحل بازی">
      <Alert
        type="info"
        message="مراحلی که کاربر در سفر خود طی می‌کند. با کلید فعال/غیرفعال، بازچینش ترتیب و ویرایش، جریان بازی از همین‌جا کنترل می‌شود."
        style={{ marginBottom: 16 }}
      />

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          form.resetFields();
          setEditingId(null);
          setIsModalVisible(true);
        }}
        style={{ marginBottom: '16px' }}
      >
        مرحله جدید
      </Button>

      <Spin spinning={loading}>
        <Table columns={columns} dataSource={steps} rowKey="_id" pagination={false} />
      </Spin>

      <Modal
        title={editingId ? 'ویرایش مرحله' : 'مرحله جدید'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" initialValues={{ enabled: true, icon: '✨' }}>
          <Form.Item name="key" label="کلید (انگلیسی، یکتا)" rules={[{ required: true }]}>
            <Input placeholder="sorting_hat, wand, ..." disabled={!!editingId} />
          </Form.Item>

          <Form.Item name="title" label="عنوان" rules={[{ required: true }]}>
            <Input placeholder="مثال: کلاه گروه‌بندی" />
          </Form.Item>

          <Form.Item name="icon" label="آیکون (اموجی)">
            <Input placeholder="🎩" />
          </Form.Item>

          <Form.Item name="description" label="توضیح">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item name="enabled" label="فعال" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Button type="primary" htmlType="submit" block loading={loading}>
            ذخیره
          </Button>
        </Form>
      </Modal>
    </Card>
  );
}
